import { DataItem, getDataClass, load } from 'scrivito'

import { CONTACT_DESC_ORDER, DataClassName } from '@/Data/const'
import { ContactListRelation } from '@/Data/ContactListRelation/ContactListRelationDataClass'
import { SchemaAttribute, SchemaAttributes, SchemaGroups } from '@/Data/typesV2'
import { getAttributeDisplayValue } from '@/utils/CRUD/read/displayValue'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { getSchemaName } from '@/utils/dataClassMap'
import { ensureString } from '@/utils/ensureString'
import { isString } from '@/utils/isString'

export const MAX_SYNC_CSV_EXPORT_ROWS = 5000
export const CSV_EXPORT_PREVIEW_ROWS = 3

/** Contact entity types that can be members of a list segment and exported. */
export type ExportContactEntity = DataClassName.COMPANY | DataClassName.PERSON

const RECOMMENDED_PERSON_ATTRIBUTE_KEYS = [
  'salutation',
  'title',
  'givenName',
  'familyName',
  'name',
  'email',
  'phone',
  'number',
  'group',
  'status',
  'accountManager',
]

type ExportStatus = 'done' | 'too_large'

export interface ExportProgress {
  completed: number
  total: number
}

export type ExportHeaderNameMode = 'readable' | 'schema'

interface ExportListPersonsToCsvOptions {
  listId: string
  contactEntity: ExportContactEntity
  listTitle?: string
  attributeKeys?: string[]
  headerNameMode?: ExportHeaderNameMode
  onProgress?: (progress: ExportProgress) => void
}

interface ExportListPersonsToCsvResult {
  status: ExportStatus
  totalCount: number
  download?: () => void
}

interface ExportData {
  totalCount: number
  groupsOrder: string[] | undefined
  schemaGroups: SchemaGroups | undefined
  schemaAttributes: SchemaAttributes
  contacts: DataItem[]
}

export async function exportListPersonsToCsv({
  listId,
  contactEntity,
  listTitle,
  attributeKeys,
  headerNameMode = 'readable',
  onProgress,
}: ExportListPersonsToCsvOptions): Promise<ExportListPersonsToCsvResult> {
  const exportData = await load<ExportData>(() => {
    const { groupsOrder, schemaAttributes, schemaGroups } = getSchemaAttributes(
      getSchemaName(contactEntity),
    )
    const contactDataClass = getDataClass(contactEntity)
    if (!contactDataClass) {
      throw new Error(
        `No data class found for contact entity "${contactEntity}"`,
      )
    }
    const relationScope = ContactListRelation.all().transform({
      filters: {
        list: listId,
        contactEntity,
      },
      order: [CONTACT_DESC_ORDER],
    })
    const totalCount = relationScope.count() ?? 0

    if (totalCount > MAX_SYNC_CSV_EXPORT_ROWS) {
      return {
        totalCount,
        groupsOrder,
        schemaAttributes: schemaAttributes ?? {},
        schemaGroups,
        contacts: [],
      }
    }

    const relations =
      totalCount > 0
        ? ContactListRelation.all()
            .transform({
              filters: {
                list: listId,
                contactEntity,
              },
              order: [CONTACT_DESC_ORDER],
              limit: totalCount,
            })
            .take()
        : []
    const contactIds = relations
      .map((relation) => relation.get('contact'))
      .filter(isString)
    const contacts = contactIds
      .map((contactId) => contactDataClass.get(contactId))
      .filter(isDefined)

    return {
      totalCount,
      groupsOrder,
      schemaAttributes: schemaAttributes ?? {},
      schemaGroups,
      contacts,
    }
  })

  if (exportData.totalCount > MAX_SYNC_CSV_EXPORT_ROWS) {
    return {
      status: 'too_large',
      totalCount: exportData.totalCount,
    }
  }

  const normalizedAttributeKeys = normalizeExportAttributeKeys(
    attributeKeys,
    exportData.groupsOrder,
    exportData.schemaGroups,
    exportData.schemaAttributes,
  )
  const headers = [
    ...normalizedAttributeKeys.map((key) =>
      getHeaderLabel(key, exportData.schemaAttributes, headerNameMode),
    ),
  ]
  const rows = await buildCsvRowsWithProgress({
    contacts: exportData.contacts,
    schemaAttributes: exportData.schemaAttributes,
    attributeKeys: normalizedAttributeKeys,
    onProgress,
  })

  const csvDownload = createCsvDownload({
    filename: buildFilename(listTitle),
    headers,
    rows,
  })
  csvDownload.download()

  return {
    status: 'done',
    totalCount: exportData.totalCount,
    download: csvDownload.download,
  }
}

async function buildCsvRowsWithProgress({
  contacts,
  schemaAttributes,
  attributeKeys,
  onProgress,
}: {
  contacts: DataItem[]
  schemaAttributes: SchemaAttributes
  attributeKeys: string[]
  onProgress?: (progress: ExportProgress) => void
}) {
  const rows: string[][] = []
  const total = contacts.length
  const chunkSize = 100

  onProgress?.({ completed: 0, total })

  for (let index = 0; index < total; index += chunkSize) {
    const chunk = contacts.slice(index, index + chunkSize)

    chunk.forEach((contact) => {
      rows.push(
        attributeKeys.map((key) =>
          getAttributeValueByKey(key, schemaAttributes, contact),
        ),
      )
    })

    onProgress?.({ completed: rows.length, total })
    await nextTick()
  }

  return rows
}

function nextTick() {
  return new Promise<void>((resolve) => setTimeout(resolve, 0))
}

export function getOrderedAttributeKeys(
  groupsOrder: string[] | undefined,
  schemaGroups: SchemaGroups | undefined,
  schemaAttributes: SchemaAttributes,
) {
  const groupedKeys = (groupsOrder ?? []).flatMap(
    (groupName) => schemaGroups?.[groupName]?.attributeOrder ?? [],
  )

  return Array.from(
    new Set([...groupedKeys, ...Object.keys(schemaAttributes ?? {})]),
  )
}

export function getDefaultExportAttributeKeys(
  groupsOrder: string[] | undefined,
  schemaGroups: SchemaGroups | undefined,
  schemaAttributes: SchemaAttributes,
) {
  return Array.from(
    new Set([
      '_id',
      ...getOrderedAttributeKeys(groupsOrder, schemaGroups, schemaAttributes),
    ]),
  ).filter((key) => key === '_id' || Boolean(schemaAttributes[key]))
}

export function getRecommendedExportAttributeKeys(
  groupsOrder: string[] | undefined,
  schemaGroups: SchemaGroups | undefined,
  schemaAttributes: SchemaAttributes,
) {
  const allAttributeKeys = getDefaultExportAttributeKeys(
    groupsOrder,
    schemaGroups,
    schemaAttributes,
  )
  const recommendedKeys = RECOMMENDED_PERSON_ATTRIBUTE_KEYS.filter((key) =>
    allAttributeKeys.includes(key),
  )
  const fallbackKeys = allAttributeKeys
    .filter((key) => key !== '_id' && !recommendedKeys.includes(key))
    .slice(0, Math.max(0, 8 - recommendedKeys.length))

  return Array.from(
    new Set(['_id', ...recommendedKeys, ...fallbackKeys]),
  ).filter((key) => key === '_id' || Boolean(schemaAttributes[key]))
}

export function normalizeExportAttributeKeys(
  attributeKeys: string[] | undefined,
  groupsOrder: string[] | undefined,
  schemaGroups: SchemaGroups | undefined,
  schemaAttributes: SchemaAttributes,
) {
  const defaultAttributeKeys = getDefaultExportAttributeKeys(
    groupsOrder,
    schemaGroups,
    schemaAttributes,
  )
  const safeAttributeKeys = attributeKeys?.length
    ? attributeKeys
    : defaultAttributeKeys

  return Array.from(new Set(safeAttributeKeys)).filter(
    (key) => key === '_id' || Boolean(schemaAttributes[key]),
  )
}

export function getHeaderLabel(
  key: string,
  schemaAttributes: SchemaAttributes,
  headerNameMode: ExportHeaderNameMode = 'readable',
) {
  if (headerNameMode === 'schema') return key
  if (key === '_id') return 'ID'

  return schemaAttributes[key]?.title || key
}

function getAttributeValue(attribute?: SchemaAttribute, rawValue?: unknown) {
  const displayValue = getAttributeDisplayValue(attribute, rawValue)

  if (displayValue !== undefined && displayValue !== '') {
    return Array.isArray(displayValue) ? displayValue.join(', ') : displayValue
  }

  return stringifyRawValue(rawValue)
}

export function getAttributeValueByKey(
  key: string,
  schemaAttributes: SchemaAttributes,
  person: DataItem,
) {
  if (key === '_id') return person.id()

  return getAttributeValue(schemaAttributes[key], person.get(key))
}

function stringifyRawValue(rawValue: unknown): string {
  if (rawValue === null || rawValue === undefined) return ''
  if (rawValue instanceof Date) return rawValue.toISOString()
  if (Array.isArray(rawValue)) return rawValue.map(stringifyRawValue).join(', ')
  if (typeof rawValue === 'object') return JSON.stringify(rawValue)

  return String(rawValue)
}

function createCsvDownload({
  filename,
  headers,
  rows,
}: {
  filename: string
  headers: string[]
  rows: string[][]
}) {
  const csvContent = [headers, ...rows]
    .map((row) => row.map(escapeCsvCell).join(','))
    .join('\r\n')
  const blob = new Blob([`\uFEFF${csvContent}`], {
    type: 'text/csv;charset=utf-8;',
  })

  return {
    download: () => {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
  }
}

function escapeCsvCell(value: string) {
  return `"${value.replace(/"/g, '""')}"`
}

function buildFilename(listTitle?: string) {
  const safeTitle = ensureString(listTitle).trim() || 'list-segment'
  const dateSuffix = new Date().toISOString().slice(0, 10)

  return `${sanitizeFilename(safeTitle)}-${dateSuffix}.csv`
}

function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}

function sanitizeFilename(value: string): string {
  return Array.from(value)
    .map((char) => {
      const code = char.charCodeAt(0)
      const isControlCharacter = code < 32
      const isReservedCharacter = '<>:"/\\|?*'.includes(char)

      return isControlCharacter || isReservedCharacter ? '-' : char
    })
    .join('')
}
